import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck, Package, ShoppingBag, AlertCircle } from "lucide-react";
import api from "@/lib/api";

/** Bell icon with unread badge + dropdown of latest 10 notifications. Staff only. */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropRef = useRef(null);
  const nav = useNavigate();

  // Poll unread count every 30 s
  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.get("/notifications/unread-count").then((r) => { if (!cancelled) setCount(r.data.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Close on outside click
  useEffect(() => {
    const h = (e) => { if (open && dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const openDropdown = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 10 } });
      setRows(data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  const markRead = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, is_read: true } : r)));
      setCount((c) => Math.max(0, c - (n.is_read ? 0 : 1)));
    } catch {}
  };

  const dismiss = async (n) => {
    try {
      await api.delete(`/notifications/${n.id}`);
      setRows((prev) => prev.filter((r) => r.id !== n.id));
      if (!n.is_read) setCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setRows((prev) => prev.map((r) => ({ ...r, is_read: true })));
      setCount(0);
    } catch {}
  };

  const iconFor = (t) => {
    if (t === "new_order") return <ShoppingBag size={14} className="text-emerald-600" />;
    if (t === "low_stock") return <Package size={14} className="text-amber-600" />;
    if (t === "unpaid") return <AlertCircle size={14} className="text-red-600" />;
    return <Bell size={14} className="text-zinc-600" />;
  };

  const openRow = (n) => {
    if (!n.is_read) markRead(n);
    if (n.link) { setOpen(false); nav(n.link); }
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        className="relative p-2 border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-black rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center leading-none"
            data-testid="notification-badge"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-32px)] bg-white border border-zinc-200 rounded-2xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-zinc-50">
            <div>
              <div className="font-display font-black tracking-tighter text-lg text-zinc-900">Notifications</div>
              <div className="text-[11px] text-zinc-500">{count > 0 ? `${count} unread` : "All caught up"}</div>
            </div>
            <div className="flex items-center gap-1">
              {rows.some((r) => !r.is_read) && (
                <button
                  onClick={readAll}
                  className="text-[11px] font-bold text-[#FF6B00] hover:underline inline-flex items-center gap-1 px-2 py-1"
                  data-testid="notif-read-all-btn"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-zinc-400 hover:text-zinc-700" data-testid="notif-close-dropdown"><X size={14} /></button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading && <div className="px-4 py-8 text-center text-xs text-zinc-500">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Bell size={20} className="mx-auto text-zinc-300" />
                <div className="mt-2 text-xs text-zinc-500">No notifications yet.</div>
              </div>
            )}
            {!loading && rows.map((n) => (
              <div
                key={n.id}
                onClick={() => openRow(n)}
                className={`px-4 py-3 border-b border-zinc-100 hover:bg-orange-50 cursor-pointer transition-colors ${n.is_read ? "bg-white" : "bg-orange-50/50"}`}
                data-testid={`notif-row-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 flex-shrink-0">{iconFor(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00] flex-shrink-0" />}
                      <div className="text-sm font-bold text-zinc-900 truncate">{n.title}</div>
                    </div>
                    <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-zinc-400 mt-1">{formatRelative(n.created_at)}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); dismiss(n); }}
                    className="p-1 text-zinc-300 hover:text-red-500 flex-shrink-0"
                    title="Dismiss"
                    data-testid={`notif-dismiss-${n.id}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs font-bold text-[#FF6B00] hover:bg-orange-50 py-3 border-t border-zinc-100"
            data-testid="notif-see-all-link"
          >
            See all notifications →
          </Link>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
